import { computed } from 'vue'
import { freezeObject } from '@/global/utils/utils'
import TabActionButtons from '@/global/components/view-layouts/tabs-layout/components/TabActionButtons.vue'
import SubcontractorDetailsInfo from '@/modules/road-maintenance-module/winter-service-v2/subcontractors/components/SubcontractorDetailsInfo.vue'
import { useSubcontractorFields } from '@/modules/road-maintenance-module/winter-service-v2/subcontractors/composables/useSubcontractorFields'
import {
  applyProviderForm,
  applyProviderStatusToPayload,
  withProviderForm
} from '@/modules/road-maintenance-module/winter-service-v2/common/composables/providerData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  SUBCONTRACTORS_ENDPOINT,
  SUBCONTRACTORS_INDEX_ROUTE,
  SUBCONTRACTORS_TRANSLATION_NAMESPACE as NS
} from '@/modules/road-maintenance-module/winter-service-v2/subcontractors/config/constants'

const TAB_KEY = 'subcontractor_details'

export function useSubcontractorDetails (options) {
  const {
    translate,
    resolveRouteParamValue,
    openGeolocationDialogFn
  } = options || {}

  const { fields } = useSubcontractorFields({
    translate,
    openGeolocationDialogFn
  })

  const apiConfig = freezeObject({
    get: {
      module: WINTER_SERVICE_MODULE,
      route: `${SUBCONTRACTORS_ENDPOINT}/${resolveRouteParamValue('id')}`
    },
    post: {
      module: WINTER_SERVICE_MODULE,
      route: `${SUBCONTRACTORS_ENDPOINT}/${resolveRouteParamValue('id')}`
    }
  })

  const tabActions = computed(() => ({
    component: TabActionButtons,
    title: translate(`${NS}.subcontractor_details`),
    showEditButtons: true,
    showBackButton: {
      uiViewName: SUBCONTRACTORS_INDEX_ROUTE
    },
    visible: true
  }))

  // `rows` is not just how the component finds its fields - Content.vue's
  // applyFormValuesForSend walks `tabContent[].rows[].fields[]` to build the save
  // payload, so a block that keeps its fields anywhere else saves nothing.
  const infoBlock = computed(() => ({
    component: SubcontractorDetailsInfo,
    visible: true,
    rows: [
      {
        type: 'fields',
        columnsCount: 1,
        fields: fields.value
      }
    ]
  }))

  // The backend reports a provider nested (address on `geolocation`, contact on
  // `responsible_person`, an `is_active` flag), the form edits it flat - both the
  // fetched and the just saved record have to be translated before the fields see it.
  const onDataReceived = (apiData) => {
    applyProviderForm(apiData?.data)
  }

  const afterSave = (context, data) => {
    context?.refreshData?.(withProviderForm(data))
  }

  const modifyDataBeforeSend = ({ copiedData }) => applyProviderStatusToPayload(copiedData)

  return computed(() => ({
    key: TAB_KEY,
    title: translate(`${NS}.basic_information`),
    apiConfig,
    onDataReceived,
    afterSave,
    modifyDataBeforeSend,
    // Coordinates are set through the address picker, never typed - they have no
    // field of their own, so they have to be declared to survive a save.
    hiddenFormKeys: [
      'latitude',
      'longitude'
    ],
    tabContent: [
      tabActions.value,
      infoBlock.value
    ]
  }))
}
